import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { generatePodUrl } from '../services/api';

const PODViewer = React.memo(({ open, onClose, podGcsUri }) => {
  const [loading, setLoading] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [rotation, setRotation] = useState(0);

  // Determine file type from URI
  const detectFileType = useCallback((uri) => {
    if (!uri) return null;
    const uriLower = uri.toLowerCase();
    if (uriLower.endsWith('.pdf')) {
      return 'pdf';
    } else if (uriLower.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/)) {
      return 'image';
    }
    // Default to image if unknown (for backward compatibility)
    return 'image';
  }, []);

  const fetchSignedUrl = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await generatePodUrl(podGcsUri);
      const url = response.data.signed_url;
      setSignedUrl(url);
      // Detect file type from podGcsUri
      const detectedType = detectFileType(podGcsUri);
      setFileType(detectedType);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load POD');
    } finally {
      setLoading(false);
    }
  }, [podGcsUri, detectFileType]);

  useEffect(() => {
    if (open && podGcsUri && !signedUrl) {
      fetchSignedUrl();
    }
  }, [open, podGcsUri, signedUrl, fetchSignedUrl]);

  const handleClose = useCallback(() => {
    setSignedUrl(null);
    setError(null);
    setFileType(null);
    setRotation(0); // Reset rotation on close
    onClose();
  }, [onClose]);

  const handleRotateLeft = useCallback(() => {
    setRotation((prev) => (prev - 90) % 360);
  }, []);

  const handleRotateRight = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  return React.createElement(Dialog, {
    open: open,
    onClose: handleClose,
    maxWidth: 'lg',
    fullWidth: true
  },
    React.createElement(DialogTitle, null,
      React.createElement(Box, {
        sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
      },
        'Proof of Delivery',
        React.createElement(IconButton, {
          onClick: handleClose,
          size: 'small'
        },
          React.createElement(CloseIcon, null)
        )
      )
    ),
    React.createElement(DialogContent, { dividers: true },
      loading ? React.createElement(Box, {
        sx: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400
        }
      },
        React.createElement(CircularProgress, null)
      ) : error ? React.createElement(Box, {
        sx: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400
        }
      },
        React.createElement(Typography, { color: 'error' }, error)
      ) : signedUrl ? React.createElement(Box, {
        sx: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          width: '100%',
          height: '100%'
        }
      },
        fileType === 'pdf' ? React.createElement(Box, {
          sx: {
            width: '100%',
            height: '80vh',
            minHeight: '600px'
          }
        },
          React.createElement('iframe', {
            src: `${signedUrl}#toolbar=1`,
            title: 'Proof of Delivery',
            style: {
              width: '100%',
              height: '100%',
              border: 'none'
            },
            onError: () => {
              // Fallback: try opening in new tab if iframe fails
              window.open(signedUrl, '_blank');
              setError('PDF opened in new tab. If it doesn\'t open, please use the Download button.');
            }
          })
        ) : React.createElement('img', {
          src: signedUrl,
          alt: 'Proof of Delivery',
          style: {
            maxWidth: '100%',
            maxHeight: '600px',
            objectFit: 'contain',
            transform: `rotate(${rotation}deg)`,
            transition: 'transform 0.3s ease'
          },
          onError: () => {
            setError('Failed to load image.');
          }
        })
      ) : React.createElement(Typography, null, 'No POD available')
    ),
    React.createElement(DialogActions, null,
      signedUrl && fileType === 'image' && React.createElement(Box, { sx: { display: 'flex', gap: 1 } },
        React.createElement(IconButton, {
          onClick: handleRotateLeft,
          title: 'Rotate Left',
          color: 'primary'
        },
          React.createElement(RotateLeftIcon, null)
        ),
        React.createElement(IconButton, {
          onClick: handleRotateRight,
          title: 'Rotate Right',
          color: 'primary'
        },
          React.createElement(RotateRightIcon, null)
        )
      ),
      signedUrl && fileType === 'pdf' && React.createElement(Button, {
        startIcon: React.createElement(DownloadIcon, null),
        onClick: () => {
          const link = document.createElement('a');
          link.href = signedUrl;
          link.download = 'POD.pdf';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        },
        variant: 'outlined',
        sx: { mr: 1 }
      }, 'Download PDF'),
      React.createElement(Button, { onClick: handleClose }, 'Close')
    )
  );
});

PODViewer.displayName = 'PODViewer';

export default PODViewer;

